/**
 * Shared widget config parsing for dashboard widgets.
 *
 * Centralizes JSON parsing, chartType inference, and per-chart-type
 * normalization so that both `useDashboardLayoutV2` and `DashboardFilters`
 * produce consistent config objects.
 */
import type { ChartType } from "@/models/dashboard";
import type { WidgetType } from "@/models/Widget";

type WidgetConfig = Record<string, any> | null;

const isObject = (v: unknown): v is Record<string, any> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** Map of chartType inference rules based on config shape. */
function inferVisualizationChartType(config: Record<string, any>): ChartType {
  if (config.timeUnit || config.lines || isObject(config.statisticalTesting))
    return "line";
  if (config.series && config.categories) {
    // Grouped bar stores multi-point series (one value per category); a plain
    // bar chart stores single-point series (each series is one bar).
    const isGrouped =
      Array.isArray(config.series) &&
      config.series.length > 0 &&
      config.series.every(
        (s: any) => Array.isArray(s?.data) && s.data.length > 1,
      ) &&
      config.categories.length > 1;
    return isGrouped ? "grouped_bar" : "bar";
  }
  if (config.slices || config.showLegend !== undefined) return "donut";
  if (
    isObject(config.data) &&
    (config.data.rows || config.data.columns || config.data.cells)
  )
    return "crosstab";
  if (Array.isArray(config.data)) return "table";
  if (config.rows || config.columns) return "table";
  if (config.series) return "line";
  return "bar";
}

function normalizeChartType(raw: unknown): ChartType | null {
  if (typeof raw === "string" && raw.length > 0) return raw as ChartType;
  return null;
}

/**
 * Normalize a VISUALIZATION widget config:
 * - Ensure chartType is always set (infer if missing)
 * - For line charts: default markerStyle to "arrows" and force showMarkers
 *   off when circle stat markers are active (they conflict visually).
 */
function normalizeVisualizationWidgetConfig(
  config: Record<string, any>,
  widgetId?: string,
): Record<string, any> {
  const normalized = { ...config };

  const explicitChartType = normalizeChartType(normalized.chartType);
  const chartType = explicitChartType ?? inferVisualizationChartType(normalized);

  if (!explicitChartType && widgetId) {
    console.log(`Widget ${widgetId} chartType inferred as: ${chartType}`);
  }

  normalized.chartType = chartType;

  // Line chart: ensure markerStyle defaults and enforce circle/marker coupling
  if (chartType === "line" && isObject(normalized.statisticalTesting)) {
    if (!normalized.statisticalTesting.markerStyle) {
      normalized.statisticalTesting = {
        ...normalized.statisticalTesting,
        markerStyle: "arrows",
      };
    }
    if (
      normalized.statisticalTesting.enabled &&
      normalized.statisticalTesting.markerStyle === "circles"
    ) {
      normalized.showMarkers = false;
    }
  }

  return normalized;
}

/**
 * Parse and normalize a widget's configJson string.
 * Returns a fully-normalized config object or null.
 */
export const parseWidgetConfigJson = (
  configJson: string | null | undefined,
  widgetType?: WidgetType,
  widgetId?: string,
): WidgetConfig => {
  let config: WidgetConfig = null;

  try {
    config = configJson ? JSON.parse(configJson) : null;
  } catch (error) {
    if (widgetId) {
      console.error(`Failed to parse configJson for widget ${widgetId}:`, error);
    }
  }

  if (widgetType === "VISUALIZATION") {
    if (!config) {
      console.warn(
        `Widget ${widgetId} has null config, defaulting to bar chart`,
      );
      return { chartType: "bar" };
    }
    return normalizeVisualizationWidgetConfig(config, widgetId);
  }

  return config;
};
